/**
 * Ocupação e risco da equipe no Painel.
 * A base de 8 tarefas é capacidade simultânea (MAX_ACTIVE_TASKS_PER_OPERATOR),
 * distinta da taxa configurada em tarefas/pessoa/h.
 */
import { MAX_ACTIVE_TASKS_PER_OPERATOR } from '../benchmark/teamCapacityShared';
import { getOccupancyTier, OCCUPANCY_STATUS_LABEL, type OccupancyTier } from './opsExecutiveShared';

export interface TeamOccupancy {
  occupationPct: number | null;
  slackPct: number | null;
}

export function teamOccupancy(
  activeTasks: number,
  availableOperators: number,
  capacityPerOperator = MAX_ACTIVE_TASKS_PER_OPERATOR,
): TeamOccupancy {
  if (!Number.isFinite(availableOperators) || availableOperators <= 0 || capacityPerOperator <= 0) {
    return { occupationPct: null, slackPct: null };
  }
  const occupationPct = (activeTasks / (availableOperators * capacityPerOperator)) * 100;
  return {
    occupationPct: Math.round(occupationPct),
    slackPct: Math.round(Math.max(0, 100 - occupationPct)),
  };
}

export type OperatorRiskCode = 'sobrecarga' | 'atencao_carga' | 'atraso' | 'risco_sla';

export const OPERATOR_RISK_LABEL: Record<OperatorRiskCode, string> = {
  sobrecarga: 'Sobrecarga',
  atencao_carga: 'Atenção de carga',
  atraso: 'Atraso',
  risco_sla: 'Risco de SLA',
};

export function classifyOperatorRisk(input: {
  occupationPct: number;
  tasksOverdue: number;
  slaViolatedPct: number;
}): OperatorRiskCode[] {
  const reasons: OperatorRiskCode[] = [];
  const tier = getOccupancyTier(input.occupationPct);
  if (tier === 'critical') reasons.push('sobrecarga');
  else if (tier === 'high') reasons.push('atencao_carga');
  if (input.tasksOverdue > 0) reasons.push('atraso');
  if (input.slaViolatedPct > 0) reasons.push('risco_sla');
  return reasons;
}

export function operatorLoadLabel(occupationPct: number): string {
  return OCCUPANCY_STATUS_LABEL[getOccupancyTier(occupationPct)];
}

export function operatorLoadTier(occupationPct: number): OccupancyTier {
  return getOccupancyTier(occupationPct);
}

/** Prazo da fila do operador. Independente da ocupação. */
export function overdueDeadlineLabel(tasksOverdue: number): string {
  if (tasksOverdue <= 0) return 'No prazo';
  if (tasksOverdue === 1) return '1 atraso';
  return `${tasksOverdue} atrasos`;
}

/** Badges independentes. Atraso não vira ocupação. */
export function operatorRiskBadges(input: {
  occupationPct: number;
  tasksOverdue: number;
  slaViolatedPct: number;
}): string[] {
  const badges: string[] = [];
  const tier = getOccupancyTier(input.occupationPct);
  if (tier === 'critical') badges.push('SOBRECARGA');
  else if (tier === 'high') badges.push('ATENÇÃO DE CARGA');
  if (input.tasksOverdue === 1) badges.push('1 ATRASO');
  else if (input.tasksOverdue > 1) badges.push(`${input.tasksOverdue} ATRASOS`);
  if (input.slaViolatedPct > 0) badges.push('RISCO DE SLA');
  return badges;
}

/**
 * 1 sobrecarga + atraso ou SLA
 * 2 sobrecarga
 * 3 atraso ou risco de SLA
 * 4 atenção de carga
 */
export function operatorRiskPriority(input: {
  occupationPct: number;
  tasksOverdue: number;
  slaViolatedPct: number;
}): number {
  const tier = getOccupancyTier(input.occupationPct);
  const deadline = input.tasksOverdue > 0 || input.slaViolatedPct > 0;
  if (tier === 'critical' && deadline) return 4;
  if (tier === 'critical') return 3;
  if (deadline) return 2;
  if (tier === 'high') return 1;
  return 0;
}

export function compareOperatorRisk(
  a: { occupationPct: number; tasksOverdue: number; slaViolatedPct?: number; userId: string },
  b: { occupationPct: number; tasksOverdue: number; slaViolatedPct?: number; userId: string },
): number {
  const priority = operatorRiskPriority({
    occupationPct: b.occupationPct,
    tasksOverdue: b.tasksOverdue,
    slaViolatedPct: b.slaViolatedPct ?? 0,
  }) - operatorRiskPriority({
    occupationPct: a.occupationPct,
    tasksOverdue: a.tasksOverdue,
    slaViolatedPct: a.slaViolatedPct ?? 0,
  });
  if (priority !== 0) return priority;
  if (b.tasksOverdue !== a.tasksOverdue) return b.tasksOverdue - a.tasksOverdue;
  const sla = (b.slaViolatedPct ?? 0) - (a.slaViolatedPct ?? 0);
  if (sla !== 0) return sla;
  if (b.occupationPct !== a.occupationPct) return b.occupationPct - a.occupationPct;
  return a.userId.localeCompare(b.userId);
}
